/**
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2022 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers. The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */

import { useWindowSize } from "@eisinc/App/Hooks/windowResizeHook";
import LazyImage from "@eisinc/MobiusOverrides/LazyImage";
import { getIsWebCrawler } from "@insite/client-framework/Common/WebCrawler";
import { HasCategoryContext, withCategory } from "@insite/client-framework/Components/CategoryContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCatalogPageStateByPath } from "@insite/client-framework/Store/Data/CatalogPages/CatalogPagesSelectors";
import loadCatalogPageByPath from "@insite/client-framework/Store/Data/CatalogPages/Handlers/LoadCatalogPageByPath";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Link from "@insite/mobius/Link";
import { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { connect, ResolveThunks, useDispatch } from "react-redux";
import styled, { css } from "styled-components";

interface CategoryBannerStyles {
    wrapperLinkStyle?: InjectableCss;
    categoryBannerTitleStyle?: TypographyPresentationProps;
    categoryTitleStyle?: TypographyPresentationProps;
    spinnerContainer?: InjectableCss;
    spinnerStyles?: LoadingSpinnerProps;
}

const categoryBannerStyles: CategoryBannerStyles = {
    wrapperLinkStyle: {
        css: css`
            position: relative;
            margin-bottom: 11px;
            &:focus {
                outline: 0;
            }
        `,
    },
    categoryBannerTitleStyle: {
        css: css`
            position: absolute;
            top: 50px;
            left: 38px;
            font-size: 38px;
            line-height: 40px;
            color: ${getColor("primary.contrast")};
            max-width: 85%;
            letter-spacing: 0.4px;
            font-weight: normal;
            margin: 0;
            white-space: nowrap;
            @media (max-width: 768px) {
                font-size: 30px;
                line-height: 32px;
                white-space: pre-wrap;
                left: 15px;
                top: 30px;
            }

            &&.blacked {
                color: ${getColor("text.main")};
                position: static;
                margin-bottom: 20px;
            }
        `,
    },
    categoryTitleStyle: {
        css: css`
            color: ${getColor("text.main")};
            letter-spacing: 0.4px;
            margin-bottom: 20px;
            font-weight: normal;
        `,
    },
    spinnerContainer: {
        css: css`
            position: relative;
            height: 100px;
            margin-bottom: 10px;
            display: flex;
            justify-content: center;
            align-items: center;
        `,
    },

    spinnerStyles: {
        color: "primary.main",
    },
};

const StyledContainer = styled.div`
    height: auto;
    width: 100%;
    position: relative;
    margin-bottom: 11px;
`;

// return the state to access the data of the catelog pages
const mapStateToProps = (state: ApplicationState) => {
    const fullCategoryData = getCatalogPageStateByPath(state, path).value;
    const categoryProperties = fullCategoryData?.properties;
    return {
        fullCategoryData,
        currentPage: getCurrentPage(state),
        categoryProperties,
    };
};

const mapDispatchToProps = {
    loadCatalogPageByPath,
};

type OwnProps = WidgetProps &
    HasCategoryContext &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps>;

let path = "/";

const CategoryName = ({
    category,
    fullCategoryData,
    loadCatalogPageByPath,
    currentPage,
    categoryProperties,
}: OwnProps) => {
    const windowDimensions = useWindowSize(500).width;
    const [addColor, setAddColor] = React.useState(false);
    const dispatch = useDispatch();

    React.useEffect(() => {
        if (category?.id) {
            dispatch({
                type: "xCoveo/setShouldSendSearchFromLinkEvent",
                shouldSendSearchFromLinkEvent: true,
            } as any);

            return () => {
                dispatch({
                    type: "xCoveo/setShouldSendSearchFromLinkEvent",
                    shouldSendSearchFromLinkEvent: false,
                } as any);
            };
        }
    }, [category?.id]);

    React.useEffect(() => {
        if (category && category.path && !fullCategoryData) {
            path = category.path;
            loadCatalogPageByPath({ path });
        }
    }, []);

    const addColorToTitle = () => {
        setAddColor(true);
    };

    if (!category) {
        return null;
    }

    const isSearchCrawler = getIsWebCrawler();

    /**
     *  link - found ?  show link holding image and title
     *  window width - > 1024 ? show largeImagePath : show mobileBannerImageUrl
     *  largeImagePath - found ? show the large image with title : show the title only
     *  mobileBannerImageUrl - found  ? show the mobile image with title : show the title only
     */
    if (
        categoryProperties &&
        // eslint-disable-next-line no-prototype-builtins
        categoryProperties?.hasOwnProperty("categoryBannerLink") &&
        categoryProperties.categoryBannerLink.length > 0
    ) {
        if (windowDimensions > 1024 && category.largeImagePath.length > 0) {
            return (
                <Link href={categoryProperties.categoryBannerLink} {...categoryBannerStyles.wrapperLinkStyle}>
                    <LazyImage
                        src={category.largeImagePath}
                        onError={addColorToTitle}
                        altText={category.imageAltText}
                    />
                    <Typography
                        variant="h1"
                        {...categoryBannerStyles.categoryBannerTitleStyle}
                        className={addColor ? "blacked" : ""}
                    >
                        {category.name}
                    </Typography>
                </Link>
            );
        }
        if (windowDimensions < 1025 && category.mobileBannerImageUrl.length > 0) {
            return (
                <Link href={categoryProperties.categoryBannerLink} {...categoryBannerStyles.wrapperLinkStyle}>
                    <LazyImage
                        src={category.mobileBannerImageUrl}
                        onError={addColorToTitle}
                        altText={category.imageAltText}
                    />
                    <Typography
                        variant="h1"
                        {...categoryBannerStyles.categoryBannerTitleStyle}
                        className={addColor ? "blacked" : ""}
                    >
                        {category.name}
                    </Typography>
                </Link>
            );
        }
    }
    // eslint-disable-next-line no-prototype-builtins
    if (categoryProperties && !categoryProperties?.hasOwnProperty("categoryBannerLink")) {
        if (windowDimensions > 1024 && category.largeImagePath.length > 0) {
            return (
                <StyledContainer {...categoryBannerStyles.wrapperLinkStyle}>
                    <LazyImage
                        src={category.largeImagePath}
                        onError={addColorToTitle}
                        altText={category.imageAltText}
                    />
                    <Typography
                        variant="h1"
                        {...categoryBannerStyles.categoryBannerTitleStyle}
                        className={addColor ? "blacked" : ""}
                    >
                        {category.name}
                    </Typography>
                </StyledContainer>
            );
        }
        if (windowDimensions < 1025 && category.mobileBannerImageUrl.length > 0) {
            return (
                <StyledContainer {...categoryBannerStyles.wrapperLinkStyle}>
                    <LazyImage
                        src={category.mobileBannerImageUrl}
                        onError={addColorToTitle}
                        altText={category.imageAltText.replace(/(<([^>]+)>)/gi, "")}
                    />
                    <Typography
                        variant={isSearchCrawler && currentPage.type === "CategoryDetailsPage" ? "h1" : "h2"}
                        data-test-selector={`categoryName_${category.shortDescription ?? category.name}`}
                        {...categoryBannerStyles.categoryBannerTitleStyle}
                        className={addColor ? "blacked" : ""}
                    >
                        {category.shortDescription ?? category.name}{" "}
                    </Typography>
                </StyledContainer>
            );
        }
    }
    return (
        <Typography
            variant={isSearchCrawler && currentPage.type === "CategoryDetailsPage" ? "h1" : "h2"}
            data-test-selector={`categoryName_${category.shortDescription ?? category.name}`}
            {...categoryBannerStyles.categoryTitleStyle}
        >
            {category.shortDescription ?? category.name}{" "}
        </Typography>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withCategory(CategoryName)),
    definition: {
        group: "Category Details",
        icon: "folder-bookmark",
        allowedContexts: ["CategoryDetailsPage", "ProductDetailsPage", "ProductListPage"],
    },
};

export default widgetModule;
